// "Il Cifrario" — sfondo della home: nomi di file veri che si trasformano in
// base64 da sinistra a destra. Modulo puro, senza React, per due ragioni: e'
// testabile in un browser vero senza montare l'applicazione, e il codice che
// si prova e' letteralmente lo stesso che va in produzione.
//
// ─────────────────────────────────────────────────────────────────────────
// PERCHE' LA VERSIONE DEL 2026-09-02 MATTINA FACEVA CRESCERE IL CANVAS
//
// Il CSS era:
//     position:absolute; top:var(--nav-h); left:0; right:0; bottom:0;
// senza width/height. Un <canvas> e' un elemento SOSTITUITO: con width:auto la
// larghezza usata non viene dagli inset, viene dalla dimensione INTRINSECA,
// cioe' dall'attributo width in pixel del dispositivo. Quindi:
//
//     canvas.width = w * dpr   ->  la larghezza di LAYOUT diventa w*dpr
//       -> il ResizeObserver (che osservava IL CANVAS) scatta
//       -> rimisura un riquadro piu' grande
//       -> canvas.width = (w*dpr) * dpr ...
//
// Su uno schermo a dpr 2 il canvas raddoppiava a ogni giro. Bastavano pochi
// passaggi per arrivare a decine di migliaia di pixel per lato e far fuori la
// scheda del browser.
//
// Le tre regole che lo rendono impossibile, tutte obbligatorie insieme:
//   1. il canvas ha width:100% e height:100% ESPLICITI, cosi' la dimensione
//      intrinseca non puo' mai influenzare il layout;
//   2. il ResizeObserver osserva il CONTENITORE, mai il canvas;
//   3. limite di sicurezza a MAX_LATO: oltre non si disegna e basta.
// ─────────────────────────────────────────────────────────────────────────

export const MAX_LATO = 4000;

const CELL_W = 15;
const CELL_H = 21;
const SEED = 20260901;

const PLAIN = 'foto-nozze.jpg  IMG_2291.HEIC  contratto-casa.pdf  tesi-finale.docx  backup-2019.zip  fatture-2025.csv  compleanno-mia.mov  ';
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

const ENC_MIN = 0.06, ENC_RANGE = 0.14;   // cifrati:   0,06 - 0,20
const PLN_MIN = 0.10, PLN_RANGE = 0.18;   // in chiaro: 0,10 - 0,28

// I colori vengono dai token CSS, cosi' il canvas segue il tema senza che il
// componente debba sapere quale tema e' attivo. Letti UNA VOLTA per disegno,
// non per carattere: getComputedStyle e' costoso e qui i caratteri sono
// migliaia.
//
// Tre ruoli, non due: i caratteri IN CHIARO (--cps-canvas-ink), quelli CIFRATI
// (--cps-canvas-acc, il verde) e il gradiente di fondo (--cps-canvas-bg, due
// stop separati da una virgola). Con due soli token i cifrati e il testo in
// chiaro avrebbero lo stesso colore e la transizione leggibile->illeggibile,
// che e' il senso del disegno, sparirebbe.
// In questo file non c'e' UN SOLO colore scritto: nemmeno come ripiego.
// Se i token non ci sono, non si disegna — meglio un canvas vuoto (sotto cui si
// vede il fondo della pagina, che e' sempre giusto) di caratteri dipinti con
// una tinta indovinata che poi resta congelata sul tema sbagliato.
//
// Letti dall'elemento .cps-root, UNA volta per disegno. Mai memorizzati in una
// variabile di modulo: sarebbe quello a congelarli sul tema attivo al primo
// caricamento — l'errore che ha reso il tema scuro illeggibile.
function leggiTema(container) {
  try {
    const radice = (container.closest && container.closest('.cps-root')) || container;
    const cs = getComputedStyle(radice);
    const ink = (cs.getPropertyValue('--cps-canvas-ink') || '').trim();
    const accent = (cs.getPropertyValue('--cps-canvas-accent') || '').trim();
    if (!ink || !accent) return null;
    return { ink, accent };
  } catch {
    return null;
  }
}

// La sfumatura in alto e in basso NON si fa piu' qui: si fa in CSS con
// mask-image, che non tocca la geometria e non richiede di ridisegnare.
const MAX_DPR = 2;

const DEBOUNCE_MS = 200;
// La barra degli indirizzi su mobile si ritrae durante lo scorrimento e cambia
// l'altezza del contenitore in continuazione. Si ridisegna solo se cambia la
// larghezza, o se l'altezza cambia in modo sostanziale.
const SOGLIA_ALTEZZA = 120;

function rng(seed) {
  let s = seed;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}

export function drawCipher(ctx, w, h, tema) {
  if (!tema) return false;
  // Il canvas e' TRASPARENTE: non dipinge piu' il proprio fondo. Prima
  // riempiva con un gradiente bianco, che in tema scuro restava bianco e
  // metteva un alone chiaro sotto un titolo chiaro. Adesso lascia vedere il
  // fondo della pagina, che segue il tema per conto suo.
  ctx.clearRect(0, 0, w, h);

  const r = rng(SEED);
  let pi = 0;
  ctx.font = '12px ui-monospace, SFMono-Regular, Menlo, monospace';
  ctx.textBaseline = 'middle';

  const righe = Math.ceil(h / CELL_H);
  const colonne = Math.ceil(w / CELL_W);
  for (let row = 0; row < righe; row++) {
    for (let col = 0; col < colonne; col++) {
      const p = col / (w / CELL_W);
      const enc = r() < Math.min(1, p * 1.35);
      const ch = enc ? B64.charAt((r() * B64.length) | 0) : PLAIN.charAt(pi++ % PLAIN.length);
      const a = enc ? ENC_MIN + r() * ENC_RANGE : PLN_MIN + r() * PLN_RANGE;
      if (ch === ' ') continue;
      ctx.fillStyle = enc ? `rgba(${tema.accent},${a.toFixed(3)})` : `rgba(${tema.ink},${a.toFixed(3)})`;
      ctx.fillText(ch, col * CELL_W + 4, row * CELL_H + 12);
    }
  }

  ctx.strokeStyle = `rgba(${tema.accent},0.06)`;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(w * 0.34, 0);
  ctx.lineTo(w * 0.40, h);
  ctx.stroke();
  return true;
}

// Aggancia il canvas al contenitore. Restituisce la funzione di pulizia.
// `onPaint` e' facoltativo: serve al banco di prova per registrare ogni
// ridisegno; in produzione non viene passato.
export function attachCipherCanvas(container, canvas, onPaint) {
  if (!container || !canvas || !canvas.getContext) return () => {};

  let ultimaW = 0, ultimaH = 0, timer = null, prima = true;
  let disegni = 0, saltiPerLimite = 0;

  const dipingi = () => {
    // Si misura SEMPRE il contenitore. Misurare il canvas e' esattamente
    // l'errore che innescava la crescita.
    const rect = container.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);

    // Limite di sicurezza: qualunque cosa sia andata storta a monte, oltre
    // questa soglia non si disegna. Meglio uno sfondo piatto che una scheda
    // che muore.
    if (w * dpr > MAX_LATO || h * dpr > MAX_LATO) {
      saltiPerLimite++;
      if (onPaint) onPaint({ saltato: true, w, h, dpr, disegni, saltiPerLimite });
      return;
    }

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const fatto = drawCipher(ctx, w, h, leggiTema(container));
    ultimaW = w; ultimaH = h;
    if (!fatto) return;
    disegni++;
    try { canvas.dataset.disegni = String(disegni); } catch {}
    if (onPaint) onPaint({ saltato: false, w, h, dpr, disegni, saltiPerLimite });
  };

  // Ridisegna con le dimensioni CORRENTI del canvas, senza toccare
  // cv.width / cv.height. Serve al cambio tema: i colori cambiano, la
  // geometria no — e la geometria e' l'unica cosa che possa riaprire il
  // circolo vizioso della crescita. Per questo qui non si misura niente e non
  // si assegna niente: si rilegge il contesto e si ridisegna, punto.
  const repaint = () => {
    if (!canvas.width || !canvas.height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const fatto = drawCipher(ctx, canvas.width / dpr, canvas.height / dpr, leggiTema(container));
    if (!fatto) return;
    disegni++;
    // Il conteggio finisce su un attributo del canvas invece che in console:
    // e' ispezionabile allo stesso modo, ma non sporca il log in produzione.
    try { canvas.dataset.disegni = String(disegni); } catch {}
    if (onPaint) onPaint({ saltato: false, ridipinto: true, w: canvas.width / dpr, h: canvas.height / dpr, dpr, disegni, saltiPerLimite });
  };

  const forseDipingi = () => {
    const rect = container.getBoundingClientRect();
    const w = Math.round(rect.width), h = Math.round(rect.height);
    if (prima) { prima = false; }
    else if (w === ultimaW && Math.abs(h - ultimaH) < SOGLIA_ALTEZZA) return;
    clearTimeout(timer);
    timer = setTimeout(dipingi, DEBOUNCE_MS);
  };

  dipingi();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(dipingi).catch(() => {});

  let obs = null;
  if (typeof ResizeObserver !== 'undefined') {
    obs = new ResizeObserver(forseDipingi);
    obs.observe(container);          // IL CONTENITORE, mai il canvas
  } else {
    window.addEventListener('resize', forseDipingi);
  }

  // La funzione di pulizia porta con se' repaint(): il chiamante ha un solo
  // oggetto da tenere e non puo' confondersi su quale usare.
  const destroy = () => {
    clearTimeout(timer);
    if (obs) obs.disconnect();
    else window.removeEventListener('resize', forseDipingi);
  };
  destroy.repaint = repaint;
  return destroy;
}
